import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search, Loader2, X, Tag, ArrowRight, Package } from "lucide-react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { fetchCategories, fetchPartsCatalog } from "../lib/api";
import { CATEGORY_ICON_MAP, CATEGORY_ACCENTS } from "../lib/categoryIcons";
import { SEO } from "../components/SEO";

export default function CategoriesPage() {
  const [categories, setCategories] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([fetchCategories(), fetchPartsCatalog()])
      .then(([c, p]) => {
        setCategories(c.categories || []);
        setCatalog(p.parts || []);
      })
      .finally(() => setLoading(false));
  }, []);

  // Filtrage client-side en temps réel sur NOM + RÉFÉRENCE + fabricant
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    const normalizedQ = q.replace(/\s+/g, "");
    return catalog.filter((p) => {
      const nom = p.nom.toLowerCase();
      const ref = p.reference.toLowerCase();
      const refNorm = ref.replace(/\s+/g, "");
      const fab = (p.fabricant || "").toLowerCase();
      return (
        nom.includes(q) ||
        ref.includes(q) ||
        refNorm.includes(normalizedQ) ||
        fab.includes(q)
      );
    });
  }, [query, catalog]);

  const categoryById = useMemo(
    () => Object.fromEntries(categories.map((c) => [c.id, c])),
    [categories],
  );

  return (
    <div
      data-testid="categories-page"
      className="mx-auto max-w-7xl px-4 py-10 sm:px-8 sm:py-14"
    >
      <SEO
        title="Catégories de pièces détachées auto, moto et poids lourd"
        description="Parcours notre catalogue de 30+ catégories de pièces détachées : freinage, filtration, distribution, suspension pneumatique PL, etc. Trouve ta pièce par nom ou référence OEM."
      />
      <div className="mb-10 text-center">
        <span className="font-mono text-xs uppercase tracking-widest text-cyan-400">
          Catalogue de pièces
        </span>
        <h1
          data-testid="categories-page-title"
          className="mt-3 font-heading text-4xl font-extrabold tracking-tight text-slate-50 sm:text-5xl"
        >
          Trouve ta pièce en <span className="text-cyan-400">2 secondes</span>
        </h1>
        <p className="mx-auto mt-3 max-w-2xl text-sm text-slate-400 sm:text-base">
          Tape le nom de la pièce ou directement la référence constructeur /
          équipementier (ex. <span className="font-mono text-cyan-300">1K0615301</span>).
          Les résultats s'affichent au fur et à mesure que tu tapes.
        </p>
      </div>

      {/* Barre de recherche instantanée */}
      <div
        data-testid="instant-search-section"
        className="surface-card mx-auto mb-10 max-w-3xl rounded-2xl p-4 sm:p-5"
      >
        <div className="relative">
          <Search
            className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-cyan-400"
            strokeWidth={2.5}
          />
          <Input
            data-testid="catalog-search-input"
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Nom de pièce ou référence (ex. plaquettes de frein, 1K0615301, W712/75)"
            className="h-14 border-slate-700 bg-slate-900/60 pl-12 pr-24 text-base text-slate-100 placeholder:text-slate-500 focus-visible:ring-cyan-500"
          />
          {query && (
            <button
              type="button"
              data-testid="catalog-search-clear"
              onClick={() => setQuery("")}
              className="absolute right-3 top-1/2 flex -translate-y-1/2 items-center gap-1 rounded-md bg-slate-800 px-2 py-1 text-xs text-slate-300 transition hover:bg-slate-700"
            >
              <X className="h-3.5 w-3.5" />
              Effacer
            </button>
          )}
        </div>
        {query && (
          <div
            data-testid="catalog-search-status"
            className="mt-3 flex items-center justify-between font-mono text-[11px] uppercase tracking-widest text-slate-500"
          >
            <span>
              {filtered.length} résultat{filtered.length > 1 ? "s" : ""} pour « {query} »
            </span>
            <span className="text-cyan-400">Filtre instantané actif</span>
          </div>
        )}
      </div>

      {/* Résultats de recherche instantanée */}
      {query && (
        <section
          data-testid="instant-search-results"
          className="mb-14"
        >
          {filtered.length === 0 ? (
            <div
              data-testid="instant-search-empty"
              className="surface-card mx-auto max-w-2xl rounded-2xl p-8 text-center"
            >
              <Package className="mx-auto h-10 w-10 text-slate-600" />
              <p className="mt-3 text-base text-slate-300">
                Aucune pièce ne correspond à « <span className="font-semibold">{query}</span> »
              </p>
              <p className="mt-1 text-xs text-slate-500">
                Essaie un autre mot-clé, un numéro de référence partiel, ou parcours
                les catégories ci-dessous.
              </p>
            </div>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {filtered.slice(0, 60).map((p, idx) => {
                const cat = categoryById[p.categorie];
                const accent = CATEGORY_ACCENTS[p.categorie] || CATEGORY_ACCENTS.moteur;
                const Icon = CATEGORY_ICON_MAP[cat?.icone] || Tag;
                return (
                  <article
                    key={`${p.reference}-${idx}`}
                    data-testid={`catalog-result-${idx}`}
                    className="surface-card surface-card-hover group rounded-xl p-4"
                  >
                    <div className="flex items-start gap-3">
                      <div
                        className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br ${accent.tint} ring-1 ${accent.ring}`}
                      >
                        <Icon className={`h-5 w-5 ${accent.icon}`} strokeWidth={1.8} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <h3 className="font-heading text-base font-bold text-slate-50">
                          <HighlightMatch text={p.nom} query={query} />
                        </h3>
                        <div className="mt-1 flex flex-wrap items-center gap-2 text-[11px]">
                          <span className="font-mono rounded bg-slate-900 px-2 py-0.5 text-cyan-300">
                            <HighlightMatch text={p.reference} query={query} />
                          </span>
                          <span className="text-slate-500">{p.fabricant}</span>
                        </div>
                      </div>
                    </div>
                    <div className="mt-3 flex items-center justify-between border-t border-slate-800/70 pt-3">
                      <Badge
                        variant="outline"
                        className="border-slate-700 bg-slate-900/70 text-[10px] uppercase tracking-wider text-slate-400"
                      >
                        {cat?.label || p.categorie}
                      </Badge>
                      <span className="font-heading text-sm font-bold text-slate-100">
                        dès {p.prix_indicatif.toFixed(2)} €
                      </span>
                    </div>
                    <Link
                      to={`/?q=${encodeURIComponent(p.nom)}&cat=${p.categorie}`}
                      data-testid={`catalog-cta-${idx}`}
                      className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-md bg-cyan-500/10 py-2 text-xs font-semibold text-cyan-300 transition hover:bg-cyan-500 hover:text-slate-950"
                    >
                      Comparer avec ma plaque
                      <ArrowRight className="h-3.5 w-3.5" />
                    </Link>
                  </article>
                );
              })}
            </div>
          )}
          {filtered.length > 60 && (
            <p className="mt-4 text-center text-xs text-slate-500">
              {filtered.length - 60} résultats supplémentaires — affine ta recherche.
            </p>
          )}
        </section>
      )}

      {/* Grille des catégories */}
      <section data-testid="categories-grid-section">
        <div className="mb-6 flex items-end justify-between">
          <div>
            <span className="font-mono text-xs uppercase tracking-widest text-cyan-400">
              Parcourir par catégorie
            </span>
            <h2 className="mt-1 font-heading text-2xl font-bold text-slate-50 sm:text-3xl">
              {categories.length} univers de pièces
            </h2>
          </div>
          {loading && <Loader2 className="h-5 w-5 animate-spin text-slate-500" />}
        </div>

        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
          {categories.map((cat, idx) => {
            const Icon = CATEGORY_ICON_MAP[cat.icone] || Tag;
            const accent = CATEGORY_ACCENTS[cat.id] || CATEGORY_ACCENTS.moteur;
            return (
              <Link
                key={cat.id}
                to={`/?cat=${cat.id}`}
                data-testid={`category-tile-${cat.id}`}
                className="surface-card surface-card-hover group relative flex flex-col overflow-hidden rounded-2xl p-5"
                style={{ animationDelay: `${idx * 30}ms` }}
              >
                <div
                  className={`mb-4 flex h-14 w-14 items-center justify-center rounded-xl bg-gradient-to-br ${accent.tint} ring-1 ${accent.ring} transition-transform duration-300 group-hover:scale-110 group-hover:rotate-3`}
                >
                  <Icon
                    className={`h-7 w-7 ${accent.icon}`}
                    strokeWidth={1.8}
                  />
                </div>
                <h3 className="font-heading text-base font-bold leading-tight text-slate-50 sm:text-lg">
                  {cat.label}
                </h3>
                <p className="mt-1 line-clamp-2 text-xs text-slate-400">
                  {cat.description}
                </p>
                <div className="mt-4 flex items-center justify-between border-t border-slate-800/70 pt-3 text-[11px]">
                  <span
                    data-testid={`category-parts-count-${cat.id}`}
                    className="font-mono uppercase tracking-widest text-cyan-300"
                  >
                    {cat.nb_pieces_total?.toLocaleString("fr-FR") || "199"} pièces
                  </span>
                  <ArrowRight className="h-4 w-4 text-slate-500 transition-all group-hover:translate-x-1 group-hover:text-cyan-300" />
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      <div className="mt-14 text-center">
        <Link
          to="/"
          data-testid="back-to-plate-search"
          className="inline-flex items-center gap-2 text-sm font-medium text-cyan-400 hover:text-cyan-300"
        >
          <ArrowRight className="h-4 w-4 rotate-180" />
          Retour à la recherche par plaque
        </Link>
      </div>
    </div>
  );
}

function HighlightMatch({ text, query }) {
  if (!query) return <>{text}</>;
  const q = query.trim();
  if (!q) return <>{text}</>;
  const re = new RegExp(`(${q.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`, "ig");
  const parts = text.split(re);
  return (
    <>
      {parts.map((part, i) =>
        re.test(part) ? (
          <mark key={i} className="rounded bg-cyan-500/30 px-0.5 text-cyan-100">
            {part}
          </mark>
        ) : (
          <React.Fragment key={i}>{part}</React.Fragment>
        ),
      )}
    </>
  );
}
